import {EventsTypes} from "@/enums/EventsTypes";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import {Cropper} from "vue-advanced-cropper";
import {Calendar} from "@/models/calendar/Calendar";
import {Resident} from "@/models/resident/Resident";
import {FacilitySettingsModel} from "@/models/FacilitySettings";
import {ServiceRequest} from "@/models/ServiceRequest";
import {ServiceStatus} from "@/enums/ServiceStatus";
import {Device} from "@/models/Device";
import {StaffAccessLevels} from "@/enums/StaffAccessLevels";
import firebase from "firebase";
import {EventsTypesMinor} from "@/enums/EventsTypesMinor";
import {v4 as uuidv4} from "uuid";
import {optimizePhotos, uploadImage} from "@/utils/upload-image";
import {fileSizeToMB, validFileSize10MB} from "@/utils/fileSizeToMB";
import {waitResizedImage} from "@/utils/wait-resized-image";
import moment from "moment";
import {ResidentStatus} from "@/enums/ResidentStatus";
import 'vue-advanced-cropper/dist/style.css';
import {getRegionName} from "@/utils/functionsRegion";
import {ResidentReligions} from "@/enums/ResidentReligions";
import {getFormattedDateTimeString} from "@/utils/dateTimeFormat";
import {EventLocationsMixin} from "@/mixins/EventLocations";
import {EventLocations} from "@/enums/EventLocations";
import {VeteranStatusEnum} from "@/enums/VeteranStatusEnum";
import {toTranslationKey} from "@/utils/stringFormat";
import {serializer} from "@/api/shared/serializer";
import { isArraysEqual } from "@/utils/isArraysEqual";
import {CallsTypes} from "@/enums/CallsTypes";
import {GalleryImage} from "@/models/GalleryImage";
import { UserTypeEnum } from "@/enums/UserTypeEnum";
import { NotificationTypeEnum } from "@/enums/NotificationTypeEnum";
import { getNotificationIcon } from "@/utils/getNotificationIcon";
import { hasAccess } from "@/utils/accessControl/accessControl";
import { AccessMatrixKeys } from "@/utils/accessControl/accessMatrixKeysEnum";

function getMimeType(file, fallback = null) {
    const byteArray = new Uint8Array(file).subarray(0, 4);
    let header = '';
    for (let i = 0; i < byteArray.length; i++) {
        header += byteArray[i].toString(16);
    }
    switch (header) {
        case '89504e47':
            return 'image/png';
        case '47494638':
            return 'image/gif';
        case 'ffd8ffe0':
        case 'ffd8ffe1':
        case 'ffd8ffe2':
        case 'ffd8ffe3':
        case 'ffd8ffe8':
            return 'image/jpeg';
        default:
            return fallback;
    }
}

export default {
    name: "ResidentView",
    components: {
        Cropper,
        ValidationObserver,
        ValidationProvider,
        FamilyHubUserList: () => import('@/components/resident/familyHubList.vue'),
        ResidentCalendar: () => import('@/views/residents/residents-calendar/ResidentCalendar.vue'),
        DownloadDlg: () => import('@/components/popup/download-dialog/DownloadDlg.vue'),
        Loader: () => import('@/components/loader/Loader.vue'),
        ConfirmDlg: () => import('@/components/popup/confirm-dialog/ConfirmDlg.vue'),
        ResidentSettingsDlg: () => import('@/views/residents/residents-settings/ResidentSettings.vue'),
        GalleryPhotosUploadDlg: () => import('@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue'),
        SuggestedUserStoriesDialog: () => import('@/views/residents/suggested-user-stories/SuggestedUserStoriesDialog.vue')
    },
    data() {
        return {
            readonly: true,
            storyEditMode: false,
            myStory: null,
            preferences: null,
            profilePicUploadStatus: 100,
            dateMenu: false,
            settingsDialog: false,
            imageCropperDialog: false,
            newsletterDialog: false,
            familyNewsletterPeriod: 'week',
            downloadProfileDialog: false,
            tab: null,
            ResidentStatus: ResidentStatus,
            ResidentReligions: Object.values(ResidentReligions),
            veteranStatuses: Object.values(VeteranStatusEnum).map(x => ({text: toTranslationKey(x), value: x})),
            dateOfBirthSelected: '',
            isSendingNewsLetter: false,
            isSavingNewsLetter: false,
            isUndepart: false,
            isUnarchiveMembers: false,
            interestsTags: [],
            preferencesTags: [],
            backgroundTags: [],
            notificationTypeEnum: NotificationTypeEnum,

            image: {
                src: null,
                type: null,
            },
            loading: {
                page: true,
                story: false,
                details: false
            },
            residentCalendarHeaders: [
                {
                    text: '',
                    align: 'start',
                    value: 'imageUrl',
                    sortable: false,
                },
                {
                    text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Event'],
                    align: 'start',
                    value: 'name',
                },
                {
                    text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.DateAndTime'],
                    align: 'start',
                    value: 'start',
                },
                {
                    text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Type'],
                    align: 'start',
                    value: 'type',
                },
                {
                    text: '',
                    align: 'end',
                    value: 'id',
                    sortable: false
                },
            ],
            residentUpcomingCallsHeaders: [
                {
                    text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Type'],
                    align: 'start',
                    value: 'type',
                },
                {
                    text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.DateAndTime'],
                    align: 'start',
                    value: 'start',
                },
                {
                    text: this.$i18n.messages[this.$i18n.locale]['ResidentsView.Participants'],
                    align: 'start',
                    value: 'participants',
                },
                {
                    text: '',
                    align: 'end',
                    value: 'id',
                    sortable: false
                },
            ],
            serviceRequestHeaders: [
                {
                    text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Service'],
                    align: 'start',
                    value: 'serviceName',
                },
                {
                    text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Type'],
                    value: 'serviceType',
                },
                {
                    text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Status'],
                    value: 'status',
                },
                {
                    text: '',
                    value: 'id',
                    sortable: false
                },
            ],
            activePremiumServicesHeaders: [
                {
                    text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Name'],
                    value: 'name',
                },
                {
                    text: this.$i18n.messages[this.$i18n.locale]['ResidentsView.MembershipType'],
                    value: 'membershipType',
                },
                {
                    text: '',
                    value: 'view',
                    sortable: false
                },
            ],
            activeLifestyleServicesHeaders: [
                {
                    text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Name'],
                    value: 'name',
                },
                {
                    text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Category'],
                    value: 'category',
                },
                {
                    text: '',
                    value: 'id',
                    sortable: false
                },
            ],
            headerNotifications: [
                {
                    value: "residentId",
                    sortable: false
                },
                {
                    value: "title",
                    sortable: false
                }
            ],
            activePremiumServices: [],
            activeLifestyleServices: [],
            residentPreferredName: null,
            refId: null,
            residentCopy: {...this.$store.state.residentModule.currentResident},
            residentAttendedEvents: [],
            residentFamilyMembers: []
        }
    },
    mixins: [EventLocationsMixin],
    async created() {
        this.loading.page = true;
        const criticalDataTasks = [
            this.$store.dispatch("getFacility"),
            this.$store.dispatch('getResident', this.$route.params.id),
        ];
        await Promise.all(criticalDataTasks);
        this.loading.page = false;
        this.dateOfBirthSelected = moment(this.resident.dob).startOf('day').format('YYYY-MM-DD');
        this.residentPreferredName = this.resident.pname;
        this.residentCode = this.resident.refId;
        this.residentDobPreview = this.formattedDob;
        this.residentFamilyMembers = this.$store.state.residentModule.currentResident.familyHubUsers ?? [];
        this.setFamilyNewsletter()
        const otherDataTasks = [
            this.$store.dispatch("getContentCategories"),
            this.$store.dispatch('getUpcomingEventsByResident', {
                residentId: this.$route.params.id,
            }),
            this.$store.dispatch('getUpcomingCallsByResident', {
                residentId: this.$route.params.id,
            }),
            this.$store.dispatch('getUser'),
            this.$store.dispatch("getNotifications", this.$route.params.id),
            this.$store.dispatch("getTags"),
            this.$store.dispatch('getAdditionalServiceRequestsByResident', {
                residentId: this.$route.params.id,
                statusList: [
                    ServiceStatus.Approved,
                    ServiceStatus.AwaitingApproval
                ],
            }),
        ];
        await Promise.all(otherDataTasks).then(() => {
            this.setResidentTags();
            this.getActivePremiumServices();
            this.getActiveLifestyleServices();
            this.getAttendedEvents();
        })
    },
    mounted() {
        this.initTooltips();
    },
    computed: {
        residentFamilyMembersList(){
            this.residentFamilyMembers.map(x=>{
                x.familyNewsletterOptions = x.familyNewsletterOptions.filter(o=>o.name !== 'Visits attended')
            })
            return this.residentFamilyMembers
        },
        calenderEvents(): Calendar[] {
            return this.$store.state.calenderModule.currentResidentsCalendarEvents;
        },
        tags() {
            return this.$store.state.tagsModule.tags;
        },
        resident(): Resident {
            return this.$store.state.residentModule.currentResident;
        },
        currentFacility(): string {
            return this.$store.state.authModule.currentFacility;
        },
        facility(): FacilitySettingsModel {
            return this.$store.state.facilityModule.selectedFacility;
        },
        currentResidentServiceRequests(): ServiceRequest[] {
            return this.$store.state.additionalServicesModule.currentResidentServiceRequests.filter(
                service => {
                    if (
                        service.status == ServiceStatus.Approved ||
                        service.status == ServiceStatus.AwaitingApproval
                    ) {
                        return service;
                    }
                }
            );
        },
        formattedDob() {
            return moment(this.dateOfBirthSelected).format(this.facility.dateFormat)
        },
        suggestedUserStories(){
            return this.resident?.suggestedUserStories?.map((s) => {
                const us = s
                us.dateTime = moment(us.dateTime.seconds * 1000).toDate()
                us.dateTimeDisplay = moment(us.dateTime).format(`${this.facility.dateFormat} ${this.facility.timeFormat}`)
                return us
            })?.sort((a, b) => b.dateTime - a.dateTime) || []
        },
        device(): Device {
            return this.$store.state.devicesModule.device;
        },
        user() {
            return this.$store.state.authModule.user;
        },
        StaffAccessLevels() {
            return StaffAccessLevels;
        },
        contentCategories() {
            return this.$store.state.contentLibraryModule.contentCategories
        },
        formattedActivePremiumServices() {
            return this.activePremiumServices.map(service => ({
                ...service,
                member: service.membersList?.find(x => x.id === this.resident.id) || null,
            }));
        },
        notifications(): Notification[] {
            return this.$store.state.notificationsModule.notifications;
        },
        isUsingMyHub() {
            return this.resident.isUsingMyHub ? this.ResidentStatus.UsingMyHub : this.ResidentStatus.NotUsingMyHub;
        },
        isOrgAdmin() {
            return this.user.accessLevel === this.StaffAccessLevels.OrgAdmin;
        },
        isHomeAdmin() {
            return this.user.accessLevel === this.StaffAccessLevels.HomeAdmin;
        },
        isResidentWingIncludesInWings() {
            return this.$store.state.eventLocationModule.eventLocations.filter(x => x.type === EventLocations.Wing).find(x => x.name === this.resident.wingName) ?? this.resident.wingName === this.$t('GeneralTranslations.All') ?? this.resident.wingName === null ?? this.resident.wingName === '';
        },
        upcomingCallsByResident() {
            return this.$store.state.residentModule.upcomingCallsByResident;
        },
        canDelete(){
            return hasAccess(this.user.accessLevel, AccessMatrixKeys.Residents_Details_DeleteProfile)
        },
        canDownloadProfile(){
            return hasAccess(this.user.accessLevel, AccessMatrixKeys.Residents_Details_DownloadProfile)
        },
        canManageFamilyNewsletter(){
            return hasAccess(this.user.accessLevel, AccessMatrixKeys.Residents_Details_ManageFamilyNewsletter)
        },
        isCareResident(){
            return (this.resident?.refOrigin === 'adt')
        },
        isOrgOrHomeAdmin(): boolean {
            return [StaffAccessLevels.OrgAdmin, StaffAccessLevels.HomeAdmin].includes(this.user.accessLevel)
        },
        canEditStandardResidentFields(): boolean {
            return !this.isCareResident && this.isOrgOrHomeAdmin
        },
        canEditWingRoom(): boolean {
            return this.isOrgOrHomeAdmin
        },
        canEditField() {
            const std = this.canEditStandardResidentFields
            const wr  = this.canEditWingRoom
            return {
                wing: wr,
                room: wr,
                name: std,
                preferredName: std,
                dob: std,
                religion: std,
                residentCode: std,
                address: std,
                phone: std,
                isUsingOwnDevice: std,
                isUndepart: std,
                veteranStatus: std
            }
        },
        adtReadOnlyTooltip(): string {
            return this.$t('ResidentsView.ADTReadOnlyTooltip').toString()
        },
        isWingRoomReadonly(): boolean {
            return this.readonly && !this.canEditWingRoom
        },
        editingWingRoom(): boolean {
            return !this.readonly && this.canEditWingRoom && this.isCareResident
        }
    },
    methods: {
        getNotificationIcon(notification){
            return getNotificationIcon(notification.type, notification?.eventType)
        },
        handleInput(newValue) {
            if (newValue === '') {
                this.residentPreferredName = null;
            }
        },
        selectTag(selectedTags, tagsArrayName) {
            this[tagsArrayName] = selectedTags.map((tag) => {
                if (typeof tag === 'string') {
                    return {name: tag.trim().toLowerCase()}
                }
                return tag
            })
            this.tagsUpdated = true
        },
        removeTag(tagToRemove, tagsArrayName) {
            this[tagsArrayName] = this[tagsArrayName].filter(tag => tag.name !== tagToRemove.name);
            this.tagsUpdated = true
        },
        setResidentTags() {
            if (this.resident.interestsTags) {
                this.interestsTags = this.resident.interestsTags.map((tagId) => {
                    return this.tags.find((tag) => tag.id === tagId);
                }).filter((tag) => tag !== undefined);
            }

            if (this.resident.preferencesTags) {
                this.preferencesTags = this.resident.preferencesTags.map((tagId) => {
                    return this.tags.find((tag) => tag.id === tagId);
                }).filter((tag) => tag !== undefined);
            }

            if (this.resident.backgroundTags) {
                this.backgroundTags = this.resident.backgroundTags.map((tagId) => {
                    return this.tags.find((tag) => tag.id === tagId);
                }).filter((tag) => tag !== undefined);
            }
        },
        onSelectWing(id) {
            const wing = this.residentWings.find(x => x.id === id)

            if (!wing) return

            this.resident.wingId = wing.id
            this.resident.wingName = wing.name
        },
        isVeteranChange(value) {
            if (!value) {
                this.resident.veteranStatus = null
            }
        },
        formattedDateTimeString(date: Date, addYears: boolean, addTime: boolean) {
            if (!this.facility) return ''

            return getFormattedDateTimeString(date, this.facility.dateFormat, addTime ? this.facility.timeFormat : null, addYears);
        },
        formattedDateTimeStringForCall(start, end) {
            const formattedStartTime = moment(start).format(`${this.facility.dateFormat}, ${this.facility.timeFormat}`);
            const formattedEndTime = moment(end).format(this.facility.timeFormat);
            return `${formattedStartTime} - ${formattedEndTime}`;
        },
        participants(event) {
            const familyResidents = this.addParticipants(event?.familyCallInvitees, [...this.$store.state.residentModule.residentsShortData, ...this.resident.familyHubUsers]);
            const familyMembers = this.addParticipants(event?.familyGoing, this.resident.familyHubUsers);
            const residents = this.addParticipants(event?.going, [...this.$store.state.residentModule.residentsShortData, ...this.resident.familyHubUsers]);
            const residentsInvitees = this.addParticipants(event?.invitees, [...this.$store.state.residentModule.residentsShortData, ...this.resident.familyHubUsers]);
            const residentsFamilyMembers = this.addParticipants(event?.invitees, this.resident.familyHubUsers);

            return [...residents, ...familyResidents, ...familyMembers, ...residentsInvitees, ...residentsFamilyMembers].filter((item, index, self) =>
              index === self.findIndex((t) => t.id === item.id)
            );
        },
        toggleTooltip(event: MouseEvent) {
            if (!(event.target instanceof Element)) return;
            const paragraph = event.target.closest('.tooltip-list-btn');
            if (!paragraph) return;

            const tooltip = paragraph.nextElementSibling;
            const isActive = tooltip.classList.contains('active');
            document.querySelectorAll('.tooltip-list').forEach(el => el.classList.remove('active'));
            if (!isActive) {
                tooltip.classList.add('active');
            }
        },
        closeTooltip() {
            document.querySelectorAll('.tooltip-list').forEach(el => el.classList.remove('active'));
        },
        initTooltips() {
            document.addEventListener('click', event => {
                if (!(event.target instanceof Element)) return;

                const isOutsideClick = !event.target.closest('.tooltip-list-btn');

                if (isOutsideClick) {
                    document.querySelectorAll('.tooltip-list').forEach(el => el.classList.remove('active'));
                }
            });
        },
        findResidentById(id, sourceArray) {
            return sourceArray.find(x => x.id === id);
        },
        addParticipants(idsArray, sourceArray) {
            const participants = [];
            if (idsArray && idsArray.length) {
                idsArray.forEach(id => {
                    const person = this.findResidentById(id, sourceArray);
                    if (person) {
                        participants.push({
                            id: person.id,
                            name: person.name,
                            imageUrl: person.imageUrl
                        });
                    }
                });
            }
            return participants;
        },
        residentDobPreviewChange(value) {
            this.resident.dob = value
            this.resident.dateOfBirth = moment(value, 'YYYY-MM-DD').set({
                hour: 0,
                minute: 0
            }).toDate();
        },
        formatDate(a, withTime): string {
            return withTime
                ? `${a.getFullYear()}-${a.getMonth() +
                1}-${a.getDate()} ${a.getHours()}:${a.getMinutes()}`
                : `${a.getFullYear()}-${a.getMonth() + 1}-${a.getDate()}`;
        },
        defaultFamilyNewsletterOptions() {
            this.resident.familyNewsletterPeriod = this.familyNewsletterPeriod

            if (this.residentFamilyMembers){
                this.residentFamilyMembers.forEach(doc => {
                    doc.isEnableNewsletter = true
                    doc.familyNewsletterOptions = this.facility.familyNewsletterOptions.map(x => ({...x}));
                })
            }
        },
        setFamilyNewsletter() {
            if (this.facility?.isEnableFamilyNewsletter && this.resident?.isEnableFamilyNewsletter) {
                if (!this.resident.familyNewsletterPeriod) {
                    this.resident.familyNewsletterPeriod = this.familyNewsletterPeriod
                }

                if (this.residentFamilyMembers && Array.isArray(this.facility?.familyNewsletterOptions)) {
                    this.residentFamilyMembers.forEach(doc => {
                        if (doc && !doc.familyNewsletterOptions) {
                            //get enabled options from current facility
                            doc.familyNewsletterOptions = this.facility.familyNewsletterOptions.filter(x => x?.status === true)
                        }
                    })
                }
            }
        },
        isEnabledForFacility(option) {
            return this.facility.familyNewsletterOptions.find(f => f.id == option.id)?.status === true
        },
        convertToTranslationKey(text: string) {
            return toTranslationKey(text)
        },
        async processNewsLetter(isSend) {

            if (isSend) {
                this.isSendingNewsLetter = true
            } else {
                this.isSavingNewsLetter = true
            }

            if (this.facility?.isEnableFamilyNewsletter && this.resident?.isEnableFamilyNewsletter) {
                try {
                    for (const doc of this.residentFamilyMembers) {
                        await firebase
                            .firestore()
                            .collection('familyHubUsers')
                            .doc(doc.id)
                            .update({
                                isEnableNewsletter: doc.isEnableNewsletter || false,
                                familyNewsletterOptions: doc.familyNewsletterOptions
                            })
                    }

                    this.resident.familyHubUsers.forEach((user) => {
                        const rUser = this.residentFamilyMembers.find((u) => u.id == user.id)
                        if (rUser) {
                            user.familyNewsletterOptions = rUser.familyNewsletterOptions
                            user.isEnableNewsletter = rUser.isEnableNewsletter
                        }
                    })

                    await this.$store.dispatch('updateResidentInfo', this.resident);

                    if (isSend) {
                        await this.sendNewsLetter()
                    } else {
                        this.$toast.open({
                            message: this.$i18n.messages[this.$i18n.locale]['ResidentsView.NewsletterSettingSaved'],
                            type: "success"
                        });
                    }
                } catch (error) {
                    console.error(error);
                    this.$toast.open({
                        message: this.$i18n.messages[this.$i18n.locale]['ResidentsView.UnableToSendNewsletter'],
                        type: "error"
                    });
                } finally {
                    this.isSavingNewsLetter = false
                }
            }

            this.newsletterDialog = false
        },
        async sendNewsLetter() {
            this.isSendingNewsLetter = true
            await firebase.app().functions(getRegionName())
                .httpsCallable("groupC-sendfamilyNewsletter")({
                    residentId: this.resident.id,
                    forceToSend: true //send even if it was sent earlier in the selected period
                })
                .then((snap: any) => {
                    if (snap.data.status == 200) {
                        this.$toast.open({message: snap.data.data, type: "success"});
                    } else if (snap.data.status == 400) {
                        this.$toast.open({message: snap.data.error, type: "error"});
                    } else if (snap.data.status == 100) {
                        this.$toast.open({message: snap.data.data, type: "info"});
                    } else {
                        this.$toast.open({
                            message: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.UnhandledError'],
                            type: "error"
                        });
                    }
                })
                .catch((err: any) => {
                    console.error(err);
                    this.$toast.open({
                        message: this.$i18n.messages[this.$i18n.locale]['ResidentsView.UnableToSendNewsletter'],
                        type: "error"
                    });
                }).finally(() => {
                    this.isSendingNewsLetter = false
                });
        },
        sendVerificationLink(): void {
            if (!this.resident.isUsingOwnDevice) return

            (this.$refs.confirmationModal as any)
                .open(
                    null,
                    this.$i18n.messages[this.$i18n.locale]['ResidentsView.AddVerificationEmailQuestion'].replace('[residentName]', this.resident.name),
                    false
                )
                .then(async response => {
                    if (response && response.agree) {
                        const result = await firebase.app().functions(getRegionName()).httpsCallable('resident-sendVerificationEmail')({
                            email: this.resident.email,
                        });

                        if (result.data.status === 'resident') {
                            this.$toast.open({message: "Verification email has been sent", type: "success"});
                        } else {
                            this.$toast.open({message: "Unable to send verification email", type: "error"});
                        }
                    }
                });
        },
        openResidentsSettings() {
            this.$router.push("/settings?tab=residentSettings");
        },
        getColor(type): string {
            if (type == EventsTypes.LifestylePrograms) return '#0D47A1';
            else if (type == EventsTypes.PremiumServices) return 'green';
            else if (type == EventsTypes.Notice) return 'red';
            else if (type == 'familyVisit') return 'indigo';
            else if (type == 'medical') return 'pink';
            else if (type == 'concierge') return 'orange';
            return 'orange';
        },
        getCalendarColor(type): string {
            if (
                type == EventsTypesMinor.ScheduledCall ||
                type == EventsTypesMinor.Visit
            ) {
                return 'indigo';
            }
            if (type == EventsTypesMinor.ConciergeRequest) {
                return 'orange';
            }
            if (
                type == EventsTypesMinor.Social ||
                type == EventsTypes.PremiumServices
            ) {
                return 'green';
            }
            return 'black';
        },
        getCalendarIcon(type): string {
            if (type == EventsTypesMinor.ScheduledCall) {
                return 'video';
            }
            if (type == EventsTypesMinor.ConciergeRequest) {
                return 'room-service';
            }
            if (type == EventsTypesMinor.Social) {
                return 'movie-filter';
            }
            return 'calendar';
        },
        switchStoryToEditMode(): void {
            this.storyEditMode = true;
            this.myStory = this.resident.aboutMe;
            this.preferences = this.resident.preferences;
        },
        async saveStory() {
            try {
                this.loading.story = true;
                await this.$store.dispatch('updateStoryAndPreferences', {
                    myStory: this.myStory,
                    preferences: this.preferences,
                    residentId: this.resident.id,
                });

                if (this.tagsUpdated === true) {
                    await this.$store.dispatch('updateResidentTags', {
                        residentId: this.resident.id,
                        interestsTags: this.interestsTags,
                        preferencesTags: this.preferencesTags,
                        backgroundTags: this.backgroundTags
                    });
                }
            } catch (e) {
                console.error(e);
            } finally {
                this.resident.aboutMe = this.myStory;
                this.resident.preferences = this.preferences;

                this.loading.story = false;
                this.storyEditMode = false;
            }
        },
        getTotalSpend(): number {
            const year = new Date().getFullYear();
            let sum = 0;

            this.resident.fundingMonthSpend.forEach(m => {
                if (m['year'] == year) sum += m['totalSpend'];
            });

            return sum;
        },
        getRemainingBudget(): number {
            const totalSpend = this.getTotalSpend();

            return this.resident.fundingLevelAmount - totalSpend;
        },
        getThisYearsMonthlyFunding() {
            const year = new Date().getFullYear();
            const monthNames = [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December',
            ];

            return this.resident.fundingMonthSpend.reduce(function (mths, month) {
                if (month['year'] == year) {
                    const textMonth = monthNames[month['month'] - 1];
                    mths[textMonth] = month['totalSpend']; //push({ "Month": textMonth, "Spend": month["totalSpend"] });
                }
                return mths;
            }, {});
        },
        async saveProfilePic(image: any) {
            const path = `${this.currentFacility}/residents/profilePic/${this.resident.id
            }/${uuidv4()}`;
            const file: File | Blob | any = await optimizePhotos(image, '');
            const fileSizeInMegabytes = fileSizeToMB(file?.size);

            if (!validFileSize10MB(fileSizeInMegabytes)) {
                this.$refs.file.value = null;
                return;
            }

            uploadImage({
                path,
                file,
                onStart: snapshot => {
                    const percent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    if (percent < 100) {
                        this.profilePicUploadStatus = percent;
                    }
                },
                onError: error => {
                    console.log(error.message);
                },
                onComplete: async storage => {
                    const uploadedImageUrl = await storage.snapshot.ref.getDownloadURL();
                    const resizedImageRef = firebase
                        .storage()
                        .ref()
                        .child(`${path}_200x200`);

                    try {
                        const resizedImageUrl = await waitResizedImage(10, resizedImageRef);
                        this.resident.imageUrl = uploadedImageUrl
                        this.resident.thumbnailUrl = resizedImageUrl
                    } catch (e) {
                        this.resident.imageUrl = uploadedImageUrl;
                        this.resident.thumbnailUrl = uploadedImageUrl;
                    }

                    await this.$store.dispatch('updateResidentInfo', this.resident);

                    this.profilePicUploadStatus = 100;
                },
            });
        },
        openSuggestedUserStoriesDlg(){
            this.$refs.suggestedUserStoriesDlg.open();
        },
        async updateResidentInfo() {
            const isValid = await (this.$refs.observer as any).validate();

            if (isValid) {
                this.loading.details = true;

                if (this.resident.email && this.resident.email.length && this.residentCopy.email != this.resident.email) {

                    const isExists = await this.checkIfResidentEmailExists(this.resident.id, this.resident.email);

                    if (isExists) {
                        this.$refs.observer.setErrors({Email: [this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.EmailAlreadyExists']]});
                        this.loading.details = false;
                        return
                    }
                }

                if (this.residentPreferredName !== this.resident.pname) {
                    await this.$refs.confirmationModal
                        .open(null, this.$t('ResidentsView.ThisWillReplaceTheResidentsNameOnTheirTabletText', {preferredName: this.residentPreferredName}), false, '', {
                            noButtonText: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Cancel'],
                            yesButtonText: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Save']
                        })
                        .then(response => {
                            if (response && response.agree) {
                                this.resident.pname = this.residentPreferredName;
                            }
                        })
                }

                if (this.residentCode !== this.resident.refId) {
                    await this.$refs.confirmationModal
                        .open(null, this.$i18n.messages[this.$i18n.locale]['ResidentsView.EditResidentsCodeQuestion'], false, '', {
                            noButtonText: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Cancel'],
                            yesButtonText: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Yes']
                        })
                        .then(response => {
                            if (response && response.agree) {
                                this.resident.refId = this.residentCode;
                            }
                        })
                }

                await this.$store.dispatch('updateResidentInfo', this.resident);

                await this.$store.dispatch('updateMaintenanceRequestsByResident', this.resident);

                if (this.isUndepart) {
                    await this.$store.dispatch('undepartResident', {
                        residentId: this.resident.id,
                        isUnarchiveMembers: this.isUnarchiveMembers
                    });
                }

                this.isUndepart = false
                this.isUnarchiveMembers = false
                this.loading.details = false;
                this.residentPreferredName = this.resident.pname;
                this.residentCode = this.resident.refId;
                this.readonly = true;
                this.residentCopy = {...this.resident}
            }
        },
        async onResidentSettingsUpdated() {
            const resident = await this.$store.dispatch('getResident', this.resident.id);
            this.residentCopy = {...resident}
        },
        async checkIfResidentEmailExists(skipResidentId, emailAddress) {
            const isExists = await this.$store.dispatch('checkIfResidentEmailExists', {
                skipResidentId: skipResidentId,
                emailAddress: emailAddress
            })
            return isExists;
        },
        allowedDates(date) {
            return moment(date).isBefore(new Date());
        },
        load(event) {
            const {files} = event.target;
            if (files && files[0]) {
                if (this.image.src) {
                    URL.revokeObjectURL(this.image.src);
                }
                const blob = URL.createObjectURL(files[0]);
                const reader = new FileReader();
                reader.onload = e => {
                    this.image = {
                        src: blob,
                        type: getMimeType(e.target.result, files[0].type),
                    };
                };
                reader.readAsArrayBuffer(files[0]);
            }
        },
        crop() {
            const {canvas} = (this.$refs.cropper as any).getResult();
            canvas.toBlob(blob => {
                const objectURL = URL.createObjectURL(blob);
                this.image.src = objectURL;
            }, this.image.type);
        },
        reset() {
            this.image = {
                src: null,
                type: null,
            };

            (this.$refs.file as any).value = '';
        },
        closeImageCropperDialog() {
            this.imageCropperDialog = false;
            this.reset();
        },
        closeNewsletterDialog() {
            this.newsletterDialog = false;
        },
        async openNewsletterDialog() {
            this.newsletterDialog = true;
        },
        upload() {
            const {canvas} = (this.$refs.cropper as any).getResult();
            canvas.toBlob(blob => {
                this.saveProfilePic(blob);
            }, this.image.type);
            this.imageCropperDialog = false;
            this.reset();
        },
        getActiveLifestyleServices() {
            return firebase
                .firestore()
                .collection('socialClubs')
                .where('facilityId', '==', this.currentFacility)
                .where('members', 'array-contains', this.$route.params.id)
                .get()
                .then(qs => {
                    this.activeLifestyleServices = qs.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                    }));
                });
        },
        getActivePremiumServices() {
            return firebase
                .firestore()
                .collection('additionalServices')
                .where('facilityId', '==', this.currentFacility)
                .where('membersIds', 'array-contains', this.$route.params?.id)
                .get()
                .then(qs => {
                    this.activePremiumServices = qs.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                    }));
                });
        },
        getAttendedEvents() {
            const fromDate = moment().startOf("month").toDate();
            const toDate = moment().endOf("month").toDate();

            return firebase
                .firestore()
                .collection("calender")
                .where('facilityId', '==', this.currentFacility)
                .where("start", ">=", fromDate)
                .where("start", "<=", toDate)
                .get()
                .then((querySnapshot) => {
                    const events = serializer.querySnapshotToDocuments(querySnapshot)
                    .filter((event: any) => event?.attendeesIds?.includes(this.resident.id)
                    && [...Object.values(EventsTypes)].includes(event.type) && event.isPublished === true)
                    
                    this.residentAttendedEvents = events.map((event: any) => {
                        return {
                            id: event.id,
                            imageUrl: event.imageUrl,
                            name: event.name,
                            start: moment(event.start.seconds * 1000).toDate(),
                            type: event.type,
                            description: event.description
                        }
                    })
                })
        },
        openResidentCalendar() {
            (this.$refs.residentCalendar as any).open(true, this.resident.name);
        },
        openResidentDownload() {
            (this.$refs.DownloadDlg as any).open(this.resident, this.resident.familyHubUsers, "resident");
        },
        openResidentSettings() {
            (this.$refs.ResidentSettingsDlg as any).open(this.resident.id);
        },
        deleteResident() {

            (this.$refs.confirmationModal as any)
                .open(null, this.$i18n.messages[this.facility.languageIso]['ResidentsView.DeleteMessage'], false)
                .then(response => {
                    if (response && response.agree) {
                        this.$store.dispatch('deleteResident', this.resident.id).then(() => {
                            this.$router.push("/residents")
                        })
                    }
                });
        },
        editMode() {
            this.readonly = !this.readonly;
        },
        moveToReport360() {
            this.$router.push(`/reports/reports360/${this.resident.id}`)
        },
        addWing() {
            const form = {
                name: this.resident.wingName,
                type: EventLocations.Wing,
                facilityId: this.$store.state.authModule.currentFacility,
            };

            this.$store.dispatch("addEventLocation", form)
                .then(() => {
                    this.$toast.open({
                        message: this.$i18n.messages[this.$i18n.locale]['ViewCalendarItem.WingSuccessfullyAddedMessage'],
                        type: "success"
                    });
                }).catch((error) => {
                this.$toast.open({
                    message: `${error.message}`,
                    type: "error"
                });
            });
        },
        openEvent(id: string) {
            this.$router.push({
                name: 'ViewCalendarEvent',
                params: {
                    id: id,
                    residentId: this.resident.id,
                },
            });
        },
        openGalleryPhotos() {
            (this.$refs.GalleryPhotosUploadDlg as any).open();
        },
        uploadGalleryPhoto(image: GalleryImage) {
            this.resident.imageUrl = image.image;
            this.resident.thumbnailUrl = image?.thumbnailUrl ?? image.image;
            this.$store.dispatch('updateResidentInfo', this.resident);
        },
        callType(call) {
            const creator = call?.participants?.find(participant => participant.isHost === true)

            if(!creator) return CallsTypes.Call
            
            if(creator.role === UserTypeEnum.Family){
                return CallsTypes.ScheduledFamilyCall;
            }

            if(creator.role === UserTypeEnum.Resident){
                return CallsTypes.ScheduledResidentCall;
            }

            return CallsTypes.Call
        }
    },
    watch: {
        async resident(value, oldValue) {

            if(!value || isArraysEqual(value?.familyHubUsersIds, oldValue?.familyHubUsersIds)) return

            this.residentFamilyMembers = value.familyHubUsers

            this.setFamilyNewsletter()
        },
    },
}